import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Table } from 'reactstrap';
import { TextFormat, Translate, getSortState } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import { APP_DATE_FORMAT } from 'app/config/constants';
import { ASC, DESC } from 'app/shared/util/pagination.constants';
import { overrideSortStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities } from './hourly-product-usage.reducer';

export const HourlyProductUsage = () => {
  const dispatch = useAppDispatch();

  const pageLocation = useLocation();
  const navigate = useNavigate();

  const [sortState, setSortState] = useState(
    overrideSortStateWithQueryParams(getSortState(pageLocation, 'id'), pageLocation.search),
  );

  const hourlyProductUsageList = useAppSelector(state => state.hourlyProductUsage.entities);
  const loading = useAppSelector(state => state.hourlyProductUsage.loading);

  const getAllEntities = () => {
    dispatch(
      getEntities({
        sort: `${sortState.sort},${sortState.order}`,
      }),
    );
  };

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?sort=${sortState.sort},${sortState.order}`;
    if (pageLocation.search !== endURL) {
      navigate(`${pageLocation.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [sortState.order, sortState.sort]);

  const sort = p => () => {
    setSortState({
      ...sortState,
      order: sortState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handleSyncList = () => {
    sortEntities();
  };

  const getSortIconByFieldName = (fieldName: string) => {
    const sortFieldName = sortState.sort;
    const order = sortState.order;
    if (sortFieldName !== fieldName) {
      return faSort;
    }
    return order === ASC ? faSortUp : faSortDown;
  };

  return (
    <div>
      <h2 id="hourly-product-usage-heading" data-cy="HourlyProductUsageHeading">
        <Translate contentKey="crawlerBackendApp.hourlyProductUsage.home.title">Hourly Product Usages</Translate>
        <div className="d-flex justify-content-end">
          <Button className="me-2" color="info" onClick={handleSyncList} disabled={loading}>
            <FontAwesomeIcon icon="sync" spin={loading} />{' '}
            <Translate contentKey="crawlerBackendApp.hourlyProductUsage.home.refreshListLabel">Refresh List</Translate>
          </Button>
          <Link
            to="/hourly-product-usage/new"
            className="btn btn-primary jh-create-entity"
            id="jh-create-entity"
            data-cy="entityCreateButton"
          >
            <FontAwesomeIcon icon="plus" />
            &nbsp;
            <Translate contentKey="crawlerBackendApp.hourlyProductUsage.home.createLabel">
              Create new Hourly Product Usage
            </Translate>
          </Link>
        </div>
      </h2>
      <div className="table-responsive">
        {hourlyProductUsageList && hourlyProductUsageList.length > 0 ? (
          <Table responsive>
            <thead>
              <tr>
                <th className="hand" onClick={sort('id')}>
                  <Translate contentKey="crawlerBackendApp.hourlyProductUsage.id">ID</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('id')} />
                </th>
                <th className="hand" onClick={sort('from')}>
                  <Translate contentKey="crawlerBackendApp.hourlyProductUsage.from">From</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('from')} />
                </th>
                <th className="hand" onClick={sort('util')}>
                  <Translate contentKey="crawlerBackendApp.hourlyProductUsage.util">Util</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('util')} />
                </th>
                <th className="hand" onClick={sort('hourlyUsage')}>
                  <Translate contentKey="crawlerBackendApp.hourlyProductUsage.hourlyUsage">Hourly Usage</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('hourlyUsage')} />
                </th>
                <th>
                  <Translate contentKey="crawlerBackendApp.hourlyProductUsage.tariff">Tariff</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="crawlerBackendApp.hourlyProductUsage.product">Product</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {hourlyProductUsageList.map((hourlyProductUsage, i) => (
                <tr key={`entity-${i}`} data-cy="entityTable">
                  <td>
                    <Button tag={Link} to={`/hourly-product-usage/${hourlyProductUsage.id}`} color="link" size="sm">
                      {hourlyProductUsage.id}
                    </Button>
                  </td>
                  <td>
                    {hourlyProductUsage.from ? (
                      <TextFormat type="date" value={hourlyProductUsage.from} format={APP_DATE_FORMAT} />
                    ) : null}
                  </td>
                  <td>
                    {hourlyProductUsage.util ? (
                      <TextFormat type="date" value={hourlyProductUsage.util} format={APP_DATE_FORMAT} />
                    ) : null}
                  </td>
                  <td>{hourlyProductUsage.hourlyUsage}</td>
                  <td>
                    {hourlyProductUsage.tariff ? (
                      <Link to={`/dynamic-tariff/${hourlyProductUsage.tariff.id}`}>{hourlyProductUsage.tariff.id}</Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>
                    {hourlyProductUsage.product ? (
                      <Link to={`/product/${hourlyProductUsage.product.id}`}>{hourlyProductUsage.product.id}</Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td className="text-end">
                    <div className="btn-group flex-btn-group-container">
                      <Button
                        tag={Link}
                        to={`/hourly-product-usage/${hourlyProductUsage.id}`}
                        color="info"
                        size="sm"
                        data-cy="entityDetailsButton"
                      >
                        <FontAwesomeIcon icon="eye" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.view">View</Translate>
                        </span>
                      </Button>
                      <Button
                        tag={Link}
                        to={`/hourly-product-usage/${hourlyProductUsage.id}/edit`}
                        color="primary"
                        size="sm"
                        data-cy="entityEditButton"
                      >
                        <FontAwesomeIcon icon="pencil-alt" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.edit">Edit</Translate>
                        </span>
                      </Button>
                      <Button
                        onClick={() => (window.location.href = `/hourly-product-usage/${hourlyProductUsage.id}/delete`)}
                        color="danger"
                        size="sm"
                        data-cy="entityDeleteButton"
                      >
                        <FontAwesomeIcon icon="trash" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.delete">Delete</Translate>
                        </span>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          !loading && (
            <div className="alert alert-warning">
              <Translate contentKey="crawlerBackendApp.hourlyProductUsage.home.notFound">
                No Hourly Product Usages found
              </Translate>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default HourlyProductUsage;
